import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, DatePicker, InputNumber, message, Upload, Select, Switch } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import { customLocale, getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';
import { CategoryService } from '@/services/category.service';
import { ClubService } from '@/services/club.service';
import { TOURNAMENT_STATUS, TOURNAMENT_STATUS_LABELS } from '@/constants/tournament.constants';
import type { Category } from '@/types/models/tournament';
import type { Club } from '@/types/api.types';

// Настраиваем dayjs для русской локализации
dayjs.locale('ru');


interface CreateTournamentModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

export const CreateTournamentModal: React.FC<CreateTournamentModalProps> = ({
    visible,
    onCancel,
    onSuccess
}) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [documentFileList, setDocumentFileList] = useState<UploadFile[]>([]);
    const [clubs, setClubs] = useState<Club[]>([]);
    const [loadingClubs, setLoadingClubs] = useState(false);
    const navigate = useNavigate();

    // Загружаем категории и клубы при открытии модального окна
    useEffect(() => {
        if (visible) {
            fetchCategories();
            fetchClubs();
        }
    }, [visible]);

    const fetchCategories = async () => {
        try {
            setLoadingCategories(true);
            const fetchedCategories = await CategoryService.getAll();
            setCategories(fetchedCategories);
        } catch (error) {
            logger.error('Failed to fetch categories:', error);
            message.error('Не удалось загрузить категории');
        } finally {
            setLoadingCategories(false);
        }
    };

    const fetchClubs = async () => {
        try {
            setLoadingClubs(true);
            const response = await ClubService.getClubs({ limit: 1000 });
            const clubsList = Array.isArray(response.data) ? response.data : response.data.rows || [];
            setClubs(clubsList);
        } catch (error) {
            logger.error('Failed to fetch clubs:', error);
            message.warning('Не удалось загрузить список клубов');
        } finally {
            setLoadingClubs(false);
        }
    };



    // Сбрасываем форму при закрытии модального окна
    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        setDocumentFileList([]);
        onCancel();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Convert form values to DTO
            const tournamentData: any = {
                name: values.name,
                description: values.description || undefined,
                location: values.location,
                tournamentDate: dayjs(values.tournamentDate).format('YYYY-MM-DD'),
                registrationDeadline: dayjs(values.registrationDeadline).format('YYYY-MM-DD'),
                weighingDates: values.weighingDates && Array.isArray(values.weighingDates) 
                    ? values.weighingDates.map((date: any) => dayjs(date).format('YYYY-MM-DD'))
                    : undefined,
                feeForClubMembers: values.feeForClubMembers || 0,
                feeForOthers: values.feeForOthers || 0,
                status: values.status || 'draft',
                autoRegistration: values.autoRegistration || false,
                isInviteOnly: values.isInviteOnly || false,
                disciplineType: values.disciplineType || undefined,
                victoryByPoints: values.disciplineType === 'kumite' && values.victoryByPoints != null ? values.victoryByPoints : undefined,
            };

            setUploading(true);

            // Создаем FormData для отправки данных и файлов вместе
            const formData = new FormData();
            
            // Добавляем все поля турнира в FormData
            Object.keys(tournamentData).forEach(key => {
                const value = (tournamentData as any)[key];
                if (value !== undefined && value !== null) {
                    if (Array.isArray(value)) {
                        value.forEach((item, index) => {
                            formData.append(`${key}[${index}]`, String(item));
                        });
                    } else {
                        formData.append(key, String(value));
                    }
                }
            });

            // Добавляем категории, если они выбраны
            if (values.categoryIds && Array.isArray(values.categoryIds)) {
                values.categoryIds.forEach((categoryId: number, index: number) => {
                    formData.append(`categoryIds[${index}]`, String(categoryId));
                });
            }

            // Добавляем настройки турнира
            formData.append('autoRegistration', String(values.autoRegistration || false));
            formData.append('isInviteOnly', String(values.isInviteOnly || false));
            if (values.disciplineType) {
                formData.append('disciplineType', values.disciplineType);
            }
            if (values.disciplineType === 'kumite' && values.victoryByPoints != null) {
                formData.append('victoryByPoints', String(values.victoryByPoints));
            }

            // Добавляем приглашенные клубы, если турнир по приглашениям
            if (values.isInviteOnly && values.invitedClubIds && Array.isArray(values.invitedClubIds)) {
                values.invitedClubIds.forEach((clubId: number, index: number) => {
                    formData.append(`invitedClubIds[${index}]`, String(clubId));
                });
            }

            // ВАЖНО: Отправляем обложку как первый файл в массиве files
            // Backend определяет первое изображение как coverImage
            // Обложка должна быть первой, затем документы
            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('files', fileList[0].originFileObj);
            }

            // Добавляем документы после обложки (если она есть)
            documentFileList.forEach((file) => {
                if (file.originFileObj) {
                    formData.append('files', file.originFileObj);
                }
            });

            const response: any = await apiService.post(API_ENDPOINTS.TOURNAMENTS.BASE, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            message.success('Турнир успешно создан');
            handleCancel(); // Используем handleCancel для сброса формы
            onSuccess();
            
            // Переходим на страницу созданного турнира
            // Структура ответа: { data: { success: true, data: tournament } } или { data: tournament }
            const tournamentId = response?.data?.data?.id || response?.data?.id || response?.id;
            if (tournamentId) {                
                navigate(`/tournaments/${tournamentId}`);
            } else {
                logger.warn('Tournament ID not found in response:', response);
            }
        } catch (error: any) {
            logger.error('Error creating tournament:', error);
            const errorMessage = error?.response?.data?.message || 'Ошибка при создании турнира';
            message.error(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    const beforeUploadCoverImage = (file: File) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('Можно загружать только изображения!');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Изображение должно быть меньше 5MB!');
            return false;
        }
        return false; // Предотвращаем автоматическую загрузку
    };

    const beforeUploadDocument = (file: File) => {
        // Проверяем, что это не изображение (документы только)
        const isImage = file.type.startsWith('image/');
        if (isImage) {
            message.error('Для загрузки изображений используйте поле "Фото турнира". Здесь можно загружать только документы!');
            return false;
        }
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error('Файл должен быть меньше 10MB!');
            return false;
        }
        return false; // Предотвращаем автоматическую загрузку
    };

    const handleCoverImageChange = (info: any) => {
        setFileList(info.fileList.slice(-1)); // Разрешаем только одно изображение
    };

    const handleDocumentChange = (info: any) => {
        setDocumentFileList(info.fileList);
    };

    return (
        <Modal
            title="Создать новый турнир"
            open={visible}
            onCancel={handleCancel}
            onOk={handleSubmit}
            okText="Создать"
            cancelText="Отмена"
            width={800}
            confirmLoading={uploading}
            destroyOnClose={true}
        >
            <Form
                form={form}
                layout="vertical"
            >
                {/* Поле для загрузки фото - в самом верху */}
                <Form.Item
                    name="coverImage"
                    label="Фото турнира"
                    extra="Рекомендуемый размер: не более 5MB, форматы: JPEG, PNG"
                >
                    <Upload
                        listType="picture-card"
                        fileList={fileList}
                        onChange={handleCoverImageChange}
                        beforeUpload={beforeUploadCoverImage}
                        accept="image/*"
                        maxCount={1}
                        onRemove={() => setFileList([])}
                    >
                        {fileList.length < 1 && (
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Загрузить</div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>

                <Form.Item
                    name="name"
                    label="Название"
                    rules={[{ required: true, message: 'Введите название турнира' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Описание"
                >
                    <Input.TextArea rows={4} />
                </Form.Item>

                <Form.Item
                    name="location"
                    label="Место проведения"
                    rules={[{ required: true, message: 'Укажите место проведения' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="tournamentDate"
                    label="Дата проведения"
                    rules={[{ required: true, message: 'Укажите дату проведения' }]}
                    getValueFromEvent={getDateValueFromEvent}
                >
                    <DatePicker 
                        {...datePickerProps}
                        placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    />
                </Form.Item>

                <Form.Item
                    name="registrationDeadline"
                    label="Окончание регистрации"
                    rules={[{ required: true, message: 'Укажите окончание регистрации' }]}
                    getValueFromEvent={getDateValueFromEvent}
                >
                    <DatePicker 
                        {...datePickerProps}
                        placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    />
                </Form.Item>

                <Form.Item
                    name="weighingDates"
                    label="Даты взвешивания"
                >
                    <DatePicker.RangePicker 
                        style={{ width: '100%' }}
                        format="DD.MM.YYYY"
                        locale={customLocale}
                        placeholder={['Начало', 'Конец']}
                        disabledDate={() => false}
                    />
                </Form.Item>

                <Form.Item
                    name="feeForClubMembers"
                    label="Взнос для членов клуба"
                    initialValue={0}
                >
                    <InputNumber<number>
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        formatter={(value) => `${value} ₽`}
                        parser={(value) => {
                            const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
                            return parsed ? parseFloat(parsed) : 0;
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="feeForOthers"
                    label="Взнос для остальных участников"
                    initialValue={0}
                >
                    <InputNumber<number>
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        formatter={(value) => `${value} ₽`}
                        parser={(value) => {
                            const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
                            return parsed ? parseFloat(parsed) : 0;
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="categoryIds"
                    label="Категории"
                >
                    <Select
                        mode="multiple"
                        placeholder="Выберите категории"
                        style={{ width: '100%' }}
                        loading={loadingCategories}
                        optionFilterProp="children"
                        showSearch
                    >
                        {categories.map((category) => (
                            <Select.Option
                                key={category.id}
                                value={category.id}
                            >
                                {category.name} ({category.ageFrom}-{category.ageTo} лет, {category.weightFrom}-{category.weightTo} кг, {category.gender})
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="disciplineType"
                    label="Дисциплина"
                    help="Кумите — поединки по очкам, Ката — выполнение формальных комплексов"
                >
                    <Select placeholder="Выберите тип дисциплины" allowClear>
                        <Select.Option value="kumite">Кумите</Select.Option>
                        <Select.Option value="kata">Ката</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.disciplineType !== currentValues.disciplineType}
                >
                    {({ getFieldValue }) =>
                        getFieldValue('disciplineType') === 'kumite' ? (
                            <Form.Item
                                name="victoryByPoints"
                                label="Победа по очкам"
                                help="Количество очков для досрочной победы в поединке (например, 8)"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Укажите количество очков для победы'
                                    }
                                ]}
                            >
                                <InputNumber min={1} max={15} style={{ width: '100%' }} placeholder="Например, 8" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>

                <Form.Item
                    name="autoRegistration"
                    label="Автоматическая регистрация"
                    valuePropName="checked"
                    help="Если включено, регистрация участников подтверждается автоматически без участия организатора"
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    name="isInviteOnly"
                    label="Турнир по приглашениям"
                    valuePropName="checked"
                    help="Если включено, турнир будет виден только приглашенным клубам и организатору"
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.isInviteOnly !== currentValues.isInviteOnly}
                >
                    {({ getFieldValue }) =>
                        getFieldValue('isInviteOnly') ? (
                            <Form.Item
                                name="invitedClubIds"
                                label="Пригласить клубы"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Выберите хотя бы один клуб для приглашения'
                                    }
                                ]}
                                help="Выберите клубы, которые будут приглашены на турнир"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Выберите клубы"
                                    loading={loadingClubs}
                                    style={{ width: '100%' }}
                                    optionFilterProp="children"
                                    showSearch
                                    filterOption={(input, option) => {
                                        const label = typeof option?.children === 'string' 
                                            ? option.children 
                                            : String(option?.children || '');
                                        return label.toLowerCase().includes(input.toLowerCase());
                                    }}
                                >
                                    {clubs.map(club => (
                                        <Select.Option key={club.id} value={club.id}>
                                            {club.name} {club.city ? `(${club.city})` : ''}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Статус"
                    initialValue="draft"
                >
                    <Select placeholder="Выберите статус турнира">
                        {Object.entries(TOURNAMENT_STATUS).map(([key, value]) => (
                            <Select.Option key={key} value={value}>
                                {TOURNAMENT_STATUS_LABELS[value]}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="documents"
                    label="Документы"
                    extra="Можно загружать документы (PDF, DOC, DOCX и др.). Максимальный размер файла: 10MB"
                >
                    <Upload
                        fileList={documentFileList}
                        onChange={handleDocumentChange}
                        beforeUpload={beforeUploadDocument}
                        multiple
                    >
                        <div>
                            <UploadOutlined /> Загрузить документы
                        </div>
                    </Upload>
                </Form.Item>
            </Form>
        </Modal>
    );
};